/**
 * Snapshot Formatter Content Detection
 *
 * Detects non-interactive content patterns that should be preserved in the
 * semantic snapshot: context annotations, content cards, and datepickers.
 *
 * @module
 */

import { effectiveRole, type SnapshotElement } from './snapshot-analyzer.js';
import {
  INTERACTIVE_ROLES,
  TEXT_CARRYING_ROLES,
  getElementText,
  hasInteractiveDescendants,
  isActiveElement,
  isClickableByAttribute,
  type ContentCard,
  type DatepickerDay,
  type DatepickerInfo,
  type DatepickerWeek,
} from './snapshot-formatter-shared.js';

const MAX_CONTEXT_ANNOTATIONS = 5;
const MIN_CONTENT_CARD_RUN = 1;
const MAX_CONTENT_CARD_CHILDREN = 6;
const MIN_CONTENT_CARD_CHILDREN = 2;
const LIVE_REGION_ROLES = new Set(['alert', 'status', 'log']);

// --- Context Annotations ---

/**
 * Check if an element is a meaningful context annotation (non-interactive text
 * that provides important context like summaries, headers, stats).
 */
export function isContextAnnotation(element: SnapshotElement): boolean {
  if (element.role !== 'paragraph' && element.role !== 'generic') return false;
  if (isClickableByAttribute(element)) return false;
  if (INTERACTIVE_ROLES.has(element.role)) return false;
  if (hasInteractiveDescendants(element)) return false;

  const text = getAnnotationText(element);
  if (!text || text.length < 3 || text.length > 200) return false;

  return true;
}

/**
 * Extract text from an annotation element, composing from children if needed.
 */
function getAnnotationText(element: SnapshotElement): string | undefined {
  const direct = getElementText(element);
  if (direct && direct.trim().length >= 3) return direct.trim();

  // Compose from text-carrying children
  const parts: string[] = [];
  for (const child of element.children) {
    if (TEXT_CARRYING_ROLES.has(child.role)) {
      const childText = getElementText(child);
      if (childText?.trim()) {
        parts.push(childText.trim());
      }
    }
  }
  const composed = parts.join(' | ');
  return composed.length >= 3 ? composed : undefined;
}

/**
 * Collect context annotations from a list of sibling elements.
 * Skips elements that would also qualify as content cards (to avoid duplication).
 * Returns up to MAX_CONTEXT_ANNOTATIONS annotations.
 */
export function collectContextAnnotations(siblings: SnapshotElement[]): string[] {
  const annotations: string[] = [];
  for (const sib of siblings) {
    if (annotations.length >= MAX_CONTEXT_ANNOTATIONS) break;
    // Skip elements that have structured children (they're content cards, not annotations)
    if (extractCardTexts(sib) !== null) continue;
    if (isContextAnnotation(sib)) {
      const text = getAnnotationText(sib);
      if (text) {
        annotations.push(text);
      }
    }
  }
  return annotations;
}

/**
 * Walk the entire subtree of `root` and collect descriptive text from
 * non-interactive paragraph/text/generic elements that wouldn't otherwise
 * render — descriptions, helper text, status messages, etc. These are
 * critical guideposts for the agent: a "Add a feature idea to the product
 * board." subheading tells the agent what the surface is for, even though
 * it has no ref.
 *
 * Skip elements that:
 * - already promote to a section heading (the heading itself surfaces the text);
 * - sit inside an interactive subtree (their text is captured as the
 *   element's accessible name or visible label);
 * - duplicate an existing collected line.
 *
 * Caller passes `excludeTexts` for any text already surfaced by the
 * containing section heading so we don't double up.
 */
export function collectInfoText(
  root: SnapshotElement,
  excludeTexts: Set<string>,
  skipRefs?: Set<string>
): string[] {
  const seen = new Set<string>(excludeTexts);
  const out: string[] = [];

  const visit = (el: SnapshotElement, insideInteractive: boolean): void => {
    if (out.length >= MAX_INFO_TEXT) return;
    // Don't descend into subtrees that are owned by another Section
    // (they will surface their own info text on themselves).
    if (el.ref && skipRefs?.has(el.ref)) return;

    const isInteractive =
      INTERACTIVE_ROLES.has(el.role) || isClickableByAttribute(el);
    const stillInsideInteractive = insideInteractive || isInteractive;

    // Headings already render as section titles; capture their text in
    // `seen` so siblings don't echo them back, then walk children
    // defensively.
    if (el.role === 'heading') {
      const headingText = getAnnotationText(el);
      if (headingText) seen.add(headingText);
      for (const child of el.children) visit(child, stillInsideInteractive);
      return;
    }

    if (!stillInsideInteractive && (el.role === 'paragraph' || el.role === 'text')) {
      const text = getAnnotationText(el);
      if (text && text.length >= 3 && text.length <= 240 && !seen.has(text)) {
        seen.add(text);
        out.push(text);
      }
      return;
    }

    for (const child of el.children) visit(child, stillInsideInteractive);
  };

  for (const child of root.children) visit(child, false);
  return out;
}

const MAX_INFO_TEXT = 8;

/**
 * Collect only live-region feedback from a subtree. This is intentionally
 * narrower than collectInfoText: ordinary page sections can contain many
 * paragraphs before a validation alert, and those paragraphs must not exhaust
 * the live-feedback budget before the alert is reached.
 */
export function collectLiveRegionText(
  root: SnapshotElement,
  excludeTexts: Set<string>,
  skipRefs?: Set<string>
): string[] {
  const seen = new Set<string>(excludeTexts);
  const out: string[] = [];

  const visit = (el: SnapshotElement, insideInteractive: boolean): void => {
    if (out.length >= MAX_INFO_TEXT) return;
    if (el.ref && skipRefs?.has(el.ref)) return;

    const isInteractive = INTERACTIVE_ROLES.has(el.role) || isClickableByAttribute(el);
    const stillInsideInteractive = insideInteractive || isInteractive;

    if (!stillInsideInteractive && isLiveRegionElement(el)) {
      const text = getAnnotationText(el);
      if (text && text.length >= 3 && text.length <= 240 && !seen.has(text)) {
        seen.add(text);
        out.push(text);
      }
      return;
    }

    for (const child of el.children) visit(child, stillInsideInteractive);
  };

  for (const child of root.children) visit(child, false);
  return out;
}

export function subtreeContainsLiveRegion(element: SnapshotElement): boolean {
  if (isLiveRegionElement(element)) return true;
  for (const child of element.children) {
    if (subtreeContainsLiveRegion(child)) return true;
  }
  return false;
}

function isLiveRegionElement(element: SnapshotElement): boolean {
  if (LIVE_REGION_ROLES.has(effectiveRole(element))) return true;
  const ariaLive = element.attributes['aria-live'];
  return Boolean(ariaLive && ariaLive !== 'off');
}

// --- Content Cards ---

/**
 * Extract text values from a potential content card element.
 * Returns null if the element doesn't match the card pattern.
 */
function extractCardTexts(element: SnapshotElement): string[] | null {
  if (isClickableByAttribute(element)) return null;
  if (INTERACTIVE_ROLES.has(element.role)) return null;
  if (hasInteractiveDescendants(element)) return null;

  const texts: string[] = [];
  collectCardTexts(element, texts, 0);

  if (texts.length < MIN_CONTENT_CARD_CHILDREN || texts.length > MAX_CONTENT_CARD_CHILDREN) {
    return null;
  }
  return texts;
}

function collectCardTexts(element: SnapshotElement, texts: string[], depth: number): void {
  if (depth > 2) return;
  for (const child of element.children) {
    if (TEXT_CARRYING_ROLES.has(child.role) || child.role === 'listitem') {
      const text = getElementText(child);
      if (text && text.trim().length >= 2) {
        texts.push(text.trim());
      } else {
        collectCardTexts(child, texts, depth + 1);
      }
    }
  }
}

/**
 * Detect a run of content cards among siblings starting at startIndex.
 * Returns the cards and the end index (exclusive) if a run of 3+ is found.
 */
export function detectContentCardRun(
  siblings: SnapshotElement[],
  startIndex: number
): { cards: ContentCard[]; endIndex: number } | null {
  const cards: ContentCard[] = [];
  let endIndex = startIndex;

  for (let i = startIndex; i < siblings.length; i++) {
    const sib = siblings[i];
    const texts = extractCardTexts(sib);
    if (!texts) break;
    cards.push({ ref: sib.ref || '', texts });
    endIndex = i + 1;
  }

  if (cards.length < MIN_CONTENT_CARD_RUN) return null;
  return { cards, endIndex };
}

// --- Datepicker Detection ---

const DAY_ABBREVIATIONS = new Set([
  'S',
  'M',
  'T',
  'W',
  'F',
  'Su',
  'Mo',
  'Tu',
  'We',
  'Th',
  'Fr',
  'Sa',
  'Sun',
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
]);

/**
 * Detect a datepicker pattern within a container element.
 * Looks for: 7 day-of-week header labels followed by 28-42 numbered clickable cells.
 */
export function detectDatepicker(element: SnapshotElement): DatepickerInfo | null {
  const kids = element.children;
  if (kids.length < 35) return null; // 7 headers + at least 28 day cells

  // Find the 7 day-of-week headers
  let headerStart = -1;
  for (let i = 0; i <= kids.length - 7; i++) {
    if (isDayHeaderRun(kids, i)) {
      headerStart = i;
      break;
    }
  }
  if (headerStart === -1) return null;

  const dayHeaders = kids.slice(headerStart, headerStart + 7).map((k) => {
    const text = getElementText(k);
    return text?.trim() ?? '';
  });

  // Collect day cells after headers
  const dayCells: { ref: string; dayNumber: number; isSelected: boolean }[] = [];
  for (let i = headerStart + 7; i < kids.length; i++) {
    const child = kids[i];
    const text = getElementText(child)?.trim();
    if (!text) continue;

    // Stop if we hit a non-numeric text (like "Total: 36h") — it's a summary paragraph
    const num = parseInt(text, 10);
    if (isNaN(num) || num < 1 || num > 31 || String(num) !== text) break;

    dayCells.push({
      ref: child.ref || '',
      dayNumber: num,
      isSelected: isActiveElement(child) || child.rawLine.includes('[aria-selected=true]'),
    });
  }

  if (dayCells.length < 28 || dayCells.length > 42) return null;

  // Group into weeks of 7
  const weeks: DatepickerWeek[] = [];
  for (let i = 0; i < dayCells.length; i += 7) {
    const weekDays: DatepickerDay[] = dayCells.slice(i, i + 7).map((cell) => ({
      ref: cell.ref,
      dayNumber: cell.dayNumber,
      isSelected: cell.isSelected,
    }));
    weeks.push({ days: weekDays });
  }

  return {
    ref: element.ref || '',
    dayHeaders,
    weeks,
  };
}

function isDayHeaderRun(kids: SnapshotElement[], startIndex: number): boolean {
  for (let i = startIndex; i < startIndex + 7; i++) {
    const child = kids[i];
    if (isClickableByAttribute(child)) return false;
    const text = getElementText(child)?.trim();
    if (!text || !DAY_ABBREVIATIONS.has(text)) return false;
  }
  return true;
}

// --- Rendering Helpers ---

/**
 * Render context annotations as indented lines.
 */
export function formatContextAnnotations(annotations: string[], indent: number): string[] {
  const indentStr = '  '.repeat(indent + 1);
  return annotations.map((text) => `${indentStr}context: "${text}"`);
}

/**
 * Render content cards as a compact list.
 */
export function formatContentCards(cards: ContentCard[], indent: number): string[] {
  const indentStr = '  '.repeat(indent + 1);
  const lines: string[] = [];
  lines.push(`${indentStr}Content:`);
  for (const card of cards) {
    const refPart = card.ref ? `[${card.ref}] ` : '';
    lines.push(`${indentStr}  ${refPart}${card.texts.join(' · ')}`);
  }
  return lines;
}

/**
 * Render a datepicker as a compact weekly grid.
 */
export function formatDatepicker(info: DatepickerInfo, indent: number): string[] {
  const indentStr = '  '.repeat(indent + 1);
  const lines: string[] = [];

  lines.push(`${indentStr}Calendar (${info.dayHeaders.join(' ')}):`);
  for (let wi = 0; wi < info.weeks.length; wi++) {
    const week = info.weeks[wi];
    const dayCells = week.days.map((d) => {
      const selected = d.isSelected ? '*' : '';
      return `[${d.ref}]${d.dayNumber}${selected}`;
    });
    lines.push(`${indentStr}  ${dayCells.join('  ')}`);
  }
  return lines;
}
